import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiGet, apiPost, apiDelete, ApiError } from '../lib/api';

interface SlackStatus {
  data: { connected: boolean; channel: string | null };
}

function SlackIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  );
}

export default function TeamSettingsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const teamId = user?.teamId ?? '';
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const { data: slackStatus, isLoading: slackLoading } = useQuery<SlackStatus>({
    queryKey: ['slack-status', teamId],
    queryFn: () => apiGet<SlackStatus>(`/teams/${teamId}/slack`),
    enabled: !!teamId,
  });

  const connectMutation = useMutation({
    mutationFn: (code: string) =>
      apiPost<SlackStatus>(`/teams/${teamId}/slack/connect`, { code }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-status', teamId] });
      showToast('success', 'Slack connected successfully!');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to connect Slack';
      showToast('error', msg);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiDelete(`/teams/${teamId}/slack`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-status', teamId] });
      showToast('success', 'Slack disconnected.');
    },
    onError: () => showToast('error', 'Failed to disconnect Slack'),
  });

  const testMutation = useMutation({
    mutationFn: () => apiPost(`/teams/${teamId}/slack/test`),
    onSuccess: () => showToast('success', 'Test message sent to Slack!'),
    onError: () => showToast('error', 'Failed to send test message'),
  });

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }

  // Handle OAuth redirect: Slack returns ?code=...&state=teamId
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (code && state === teamId) {
      setSearchParams({}, { replace: true });
      connectMutation.mutate(code);
    }
  }, []);

  const slackClientId = import.meta.env.VITE_SLACK_CLIENT_ID as string | undefined;

  function buildSlackOAuthUrl() {
    if (!slackClientId) return null;
    const redirectUri = import.meta.env.VITE_SLACK_REDIRECT_URI as string | undefined
      ?? `${window.location.origin}/settings`;
    const params = new URLSearchParams({
      client_id: slackClientId,
      scope: 'incoming-webhook',
      redirect_uri: redirectUri,
      state: teamId,
    });
    return `https://slack.com/oauth/v2/authorize?${params.toString()}`;
  }

  const slackOAuthUrl = buildSlackOAuthUrl();
  const connected = slackStatus?.data.connected ?? false;
  const channel = slackStatus?.data.channel ?? null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {toast.message}
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900 mb-8">Team Settings</h1>

      {/* Slack integration */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-[#4A154B]">
              <SlackIcon />
            </span>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Slack Notifications</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Receive coverage alerts and daily outreach summaries in Slack.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          {slackLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Connected
                </span>
                {channel && (
                  <span className="text-sm text-gray-600">
                    posting to <span className="font-medium">{channel}</span>
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-500">
                Notifications: new coverage logged, daily outreach summary.
              </p>

              {isAdmin && (
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => testMutation.mutate()}
                    disabled={testMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    {testMutation.isPending ? 'Sending…' : 'Send test message'}
                  </button>
                  <button
                    onClick={() => disconnectMutation.mutate()}
                    disabled={disconnectMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 transition-colors"
                  >
                    {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Connect your Slack workspace to get notified when coverage is logged and receive
                a daily summary of outreach activity.
              </p>

              {isAdmin ? (
                slackOAuthUrl ? (
                  <a
                    href={slackOAuthUrl}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#4A154B] rounded-md hover:bg-[#3d1040] transition-colors"
                  >
                    <SlackIcon />
                    Add to Slack
                  </a>
                ) : (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Slack OAuth is not configured. Set{' '}
                    <code className="font-mono text-xs">VITE_SLACK_CLIENT_ID</code> in your
                    environment to enable this feature.
                  </p>
                )
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Only team owners and admins can connect Slack.
                </p>
              )}
            </div>
          )}

          {connectMutation.isPending && (
            <p className="text-sm text-gray-500 mt-3">Connecting to Slack…</p>
          )}
        </div>
      </div>
    </div>
  );
}
